import { ContainerComponent, wrap } from 'AppReact';
import { SecondaryTextSm, SecondaryTextSmBold, GrayTextSm, Empty } from 'atoms';
import Controller from './controller';
import {
  FlexCenterJustifyCenterWrapper, Mt2, FlexCenterBetweenWrapper, Mx2, RelativeWrapper,
  CustomBackgroundWrapper, ElementWithPopupWrapper, AbsoluteLeftTopWrapper, SpanWrapper,
  BorderBottomWrapper, BorderedCard, FlexCenterWrapper, Mr2, Pb4, Mt4, Mt1
} from 'wrappers';
import internalState from './internalState';
import { parseISO, format } from 'date-fns';
import { IconTextButton, SolidButton, IconLabel } from 'molecules';
import { TrashBinSvg, HistorySvg } from 'components/icons';
import { colorShade } from 'utils';
import { TimeTrackerDropdown } from 'containers';
import { calculateHoursBetweenDates, convertSecondsToTime } from 'utils/dateAndTimeUtils';

export const definition = {
  name: 'TimeTrackerRow',
  controller: Controller,
  initialProps: ({ event }) => ({ event }),
  props: internalState,
  elements: [
    {
      wrapper: RelativeWrapper,
      name: '__transparent__',
      ref: ({ containerRef }) => containerRef ?? null,
      elements: [
        {
          wrapper: CustomBackgroundWrapper,
          name: "ButtonWrapper",
          renderIf: ({ event }) => !['deleted_stop_timer', 'deleted_add_timer'].includes(event.event_name),
          displayState: ({ }) => ({ flexDirection: 'column', padding: '24px 30px', height: '77px' }),
          elements: [
            {
              wrapper: FlexCenterBetweenWrapper,
              name: 'Row',
              elements: [
                {
                  component: SecondaryTextSm,
                  name: 'Info',
                  displayValue: ({ event }) => {
                    if (!event?.to || !event.from) return '';
                    const startTime = format(parseISO(event?.from ?? new Date()), 'hh:mm aa');
                    const endTime = format(parseISO(event?.to ?? new Date()), 'hh:mm aa');
                    const date = format(parseISO(event?.to ?? new Date()), 'MM/dd/yy');
                    return `${startTime} | ${endTime} | ${date}`;
                  }
                },
                {
                  component: SecondaryTextSmBold,
                  name: 'Total',
                  displayValue: ({ event }) => {
                    if (!event?.to || !event.from) return '';
                    const { total } = calculateHoursBetweenDates({ startDate: event?.from, endDate: event?.to, withSeconds: true });
                    return total;
                  }
                }
              ]
            },
            {
              component: wrap(GrayTextSm, Mt1),
              name: 'Info',
              displayValue: ({ event }) => {
                return `${event?.assignee_name ?? ''} | ${event.created_by_type === 'tech' ? (event.event_name === 'stop_timer' ? 'Tech Timer' : 'Manual Timer') : 'Admin Entry'}`
              }
            },
            {
              component: wrap(GrayTextSm, Mt1),
              name: 'Person',
              displayValue: ({ event }) => {
                if (event?.description_history?.length === 0 && event.created_by_type === 'admin') {
                  return `Added by ${event?.created_by ?? ''}`;
                } else if (event?.description_history?.length > 0) {
                  let result;
                  result = event?.description.split('by ');
                  let editedByValue = result?.[1]?.trim();
                  return `${editedByValue ? `Edited by ${editedByValue}` : ``}`
                }
                else {
                  return ''
                }
              }
            },
          ]
        },
        {
          wrapper: CustomBackgroundWrapper,
          name: "ButtonWrapper",
          isButton: true,
          bindings: ({ historyContainerRef, isOpenHistory }) => ({ historyContainerRef, isOpenHistory }),
          renderIf: ({ event }) => ['deleted_stop_timer', 'deleted_add_timer'].includes(event.event_name),
          displayState: ({ }) => ({ alignItems: 'center', padding: '24px 30px', height: '77px', cursor: 'pointer' }),
          elements: [
            {
              component: wrap(Empty, Mr2),
              name: 'Icon',
              displayValue: ({ }) => TrashBinSvg({ color: '#566F75', with: 14, height: 14 })
            },
            {
              component: GrayTextSm,
              name: 'Person',
              displayValue: ({ }) => 'Time entry removed'
            },
          ]
        },
        {
          wrapper: AbsoluteLeftTopWrapper,
          name: '__transparent__',
          displayState: ({ }) => ({ width: '100%' }),
          renderIf: ({ isOpen, isOpenHistory, event }) => !isOpen && !isOpenHistory && !['deleted_stop_timer', 'deleted_add_timer'].includes(event.event_name),
          elements: [
            {
              wrapper: wrap(CustomBackgroundWrapper, SpanWrapper),
              name: "ButtonWrapper",
              displayState: ({ }) => ({ flexDirection: 'column', padding: '24px 30px', height: '77px', background: colorShade(0.9, window?.settings?.primary_color ?? '#1DBD6B'), }),
              elements: [
                {
                  wrapper: FlexCenterJustifyCenterWrapper,
                  name: 'ButtonsWrapper',
                  elements: [
                    {
                      component: wrap(SolidButton, Mx2),
                      name: 'Edit',
                      bindings: ({ containerRef, isOpen }) => ({ title: 'Edit', big: true, containerRef, isOpen })
                    },
                    {
                      component: wrap(IconTextButton, Mx2),
                      name: 'History',
                      renderIf: ({ event }) => event?.description_history?.length > 0,
                      bindings: ({ historyContainerRef, isOpenHistory }) => ({ title: 'History', icon: HistorySvg({}), historyContainerRef, isOpenHistory })
                    },
                    {
                      component: wrap(IconTextButton, Mx2),
                      name: 'Delete',
                      bindings: ({ }) => ({ title: 'Remove', icon: TrashBinSvg({ width: 12, height: 13 }) })
                    }
                  ]
                }

              ]
            },
          ]
        },
        {
          component: TimeTrackerDropdown,
          renderIf: ({ isOpen }) => isOpen,
          name: 'Dropdown',
          bindings: ({ event }) => ({ startTime: new Date(event.from), endTime: new Date(event.to), assigneeName: event.assignee_name, isEditing: true })
        },
        {
          wrapper: AbsoluteLeftTopWrapper,
          name: 'History',
          ref: ({ historyContainerRef }) => historyContainerRef,
          renderIf: ({ isOpenHistory }) => isOpenHistory,
          displayState: ({ }) => ({ top: '70px' }),
          elements: [
            {
              wrapper: BorderedCard,
              displayState: ({ }) => ({ background: '#FFFFFF', borderRadius: '12px', padding: '0px 20px' }),
              name: 'BoardWrapper',
              elements: [
                {
                  wrapper: wrap(Pb4, BorderBottomWrapper),
                  name: 'Wrapper',
                  isListOf: { description: ({ event }) => event.description_history },
                  elements: [
                    {
                      component: wrap(IconLabel, Mt4),
                      name: 'DateTimeTag',
                      bindings: ({ description }) => {
                        const date = format(new Date(description.datetime), 'MM/dd/yy');
                        return ({ text: date ?? '', icon: null });
                      },
                      displayState: () => ({ color: window?.settings?.primary_color ?? '#1DBD6B', background: '#E8F8F0' })
                    },
                    {
                      wrapper: wrap(FlexCenterWrapper, Mt2),
                      name: 'TextWrapper',
                      elements: [
                        {
                          component: wrap(SecondaryTextSm, Mr2),
                          name: 'TimeAndDate',
                          displayValue: ({ description }) => {
                            if (description.description.includes('Removed') || description.description.includes('Deleted')) {
                              return description.description?.replace('Deleted', 'Removed');
                            }
                            const data = description.description.split('|').filter((t) => !!t).map((text) => text.trim());
                            const date = data[0].split(' - ').filter((t) => !!t).map((t) => t.trim());
                            return `${format(new Date(date[0]), 'hh:mm aa')} - ${format(new Date(date[1]), 'hh:mm aa')} | ${format(new Date(data[1]), 'MM/dd/yy')}`;
                          }
                        },
                        {
                          component: SecondaryTextSmBold,
                          name: 'Total',
                          renderIf: ({ description }) => !description.description.includes('Removed'),
                          displayValue: ({ description }) => {
                            const data = description.description.split('|').map((text) => text.trim());
                            return convertSecondsToTime(parseInt(data[2]));
                          }
                        }
                      ]
                    },
                    {
                      component: wrap(GrayTextSm, Mt2),
                      name: 'Person',
                      renderIf: ({ description }) => !description.description.includes('Removed'),
                      displayValue: ({ description }) => {
                        const data = description.description.split('|').map((text) => text.trim());
                        return data.slice(3).join(' | ');
                      }
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ]
}

export default wrap(ContainerComponent(definition), ElementWithPopupWrapper);